import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

// This field used to hold a bare tenant name, so a value with no dot still needs the goskope.com suffix.
const tenantHost = (tenant: string) =>
  tenant.includes(".") ? tenant : `${tenant}.goskope.com`;

export const netskope = defineConnector({
  identifier: "netskope",
  name: "Netskope",
  tagLine: "Cloud security and SASE",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: () => "https://[^/]*\\.goskope\\.com/.*",
  auth: (app) =>
    headerAuth([{ name: "Netskope-Api-Token", value: app.secret.apiToken }]),
  steps: [
    {
      title: "Create an API token in Netskope",
      items: [
        {
          kind: "markdown",
          content: `Under RBAC v3 an API token belongs to a service account and inherits that account’s role, so create the role first.

1. Go to **Settings › Administration › Roles** and click **New**
2. Add the API v2 endpoints this connector should reach, with the privilege level you want for each
3. Go to **Settings › Administration › Administrators & Roles** and click **Service Account**
4. Name the account, select the role you just created, set a REST API token expiry, and click **Create**
5. Copy the token — Netskope shows it only once

Tenants not yet migrated to RBAC v3 create tokens under **Settings › Tools › REST API v2** instead.`,
        },
      ],
    },
    {
      title: "Enter your Netskope details",
      items: [
        {
          kind: "hostname",
          label: "Tenant URL",
          required: true,
          placeholder: "acme.goskope.com",
          target: { kind: "configuration", field: "tenant" },
          helperText: "Your Netskope tenant URL.",
        },
        {
          kind: "input",
          label: "API token",
          required: true,
          target: { kind: "secrets", field: "apiToken" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ tenant }) => ({
      url: `https://${tenantHost(tenant)}/api/v2/scim/Users?count=1`,
      expectedStatus: [200, 403],
    }),
  },
  publicEnvVars: ({ tenant }) => ({
    NETSKOPE_URL: tenant ? `https://${tenantHost(tenant)}` : "",
  }),
});
